import { FlaskConical } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SimulateErrorKind } from '@hub/contracts'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { mono } from '@/components/assistant-ui/elements/surfaces'
import { cn } from '@/lib/utils'

/** Failure modes the next reply can be forced into; labels live in `chat:dev.modes`. */
export const RESPONSE_MODES: SimulateErrorKind[] = [
  'provider_capability',
  'provider_transient',
  'hard_error',
  'unknown',
  'tool_error',
]

interface DevMenuProps {
  /** Armed response mode for the next send, if any. */
  mode: SimulateErrorKind | undefined
  onModeChange: (mode: SimulateErrorKind | undefined) => void
  className?: string
}

/**
 * Local-dev + super-admin only: feature flags and test switches for the chat.
 * Today: force the next reply into a given failure mode (one-shot).
 */
export function DevMenu({ mode, onModeChange, className }: DevMenuProps) {
  const { t } = useTranslation('chat')
  const armed = RESPONSE_MODES.find((m) => m === mode)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('dev.menu')}
          className={cn(
            mono,
            'flex items-center gap-1.5 rounded-full px-2 py-0.5 transition-colors',
            armed
              ? 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300'
              : 'text-foreground/35 hover:bg-foreground/[0.06] hover:text-foreground/70',
            className
          )}
        >
          <FlaskConical className="size-3" />
          dev{armed ? ` · ${t(`dev.modes.${armed}.label`).toLowerCase()}` : ''}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-foreground/50 text-xs font-medium uppercase tracking-wide">
          {t('dev.nextReply')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={mode ?? 'normal'}
          onValueChange={(v) => onModeChange(v === 'normal' ? undefined : (v as SimulateErrorKind))}
        >
          <DropdownMenuRadioItem value="normal">{t('dev.normal')}</DropdownMenuRadioItem>
          <DropdownMenuSeparator />
          {RESPONSE_MODES.map((m) => (
            <DropdownMenuRadioItem key={m} value={m} className="items-start">
              <span className="flex flex-col">
                <span>{t(`dev.modes.${m}.label`)}</span>
                <span className="text-foreground/45 text-[11px]">{t(`dev.modes.${m}.hint`)}</span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <p className="text-foreground/40 px-2 py-1 text-[11px]">{t('dev.note')}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
